import {
    ProblemDetails,
    ServiceError,
    ServiceErrorExceptionInfo
} from '../types';

function isServiceError(obj: unknown): obj is ServiceError {
    if (typeof obj !== 'object' || obj === null) {
        return false;
    }

    const error = obj as ServiceError;

    // Check required property
    if (typeof error.message !== 'string' && error.message !== null) {
        return false;
    }

    // Check optional properties
    if (
        'key' in error &&
        !(typeof error.key === 'string' || error.key === null)
    ) {
        return false;
    }

    if (
        'rawValue' in error &&
        !(typeof error.rawValue === 'string' || error.rawValue === null)
    ) {
        return false;
    }

    return true;
}

function isServiceErrorExceptionInfo(
    obj: unknown
): obj is ServiceErrorExceptionInfo {
    if (typeof obj !== 'object' || obj === null) {
        return false;
    }

    const exception = obj as ServiceErrorExceptionInfo;

    // Check required properties
    if (
        typeof exception.message !== 'string' ||
        typeof exception.detail !== 'string'
    ) {
        return false;
    }

    // Check optional properties
    if (
        'stackTrace' in exception &&
        typeof exception.stackTrace !== 'string' &&
        exception.stackTrace !== null
    ) {
        return false;
    }

    if (
        'exceptionType' in exception &&
        typeof exception.exceptionType !== 'string' &&
        exception.exceptionType !== null
    ) {
        return false;
    }

    if ('innerException' in exception) {
        if (
            exception.innerException !== null &&
            !isServiceErrorExceptionInfo(exception.innerException)
        ) {
            return false;
        }
    }

    return true;
}

export default function isProblemDetails(obj: unknown): obj is ProblemDetails {
    if (typeof obj !== 'object' || obj === null) {
        return false;
    }

    const problem = obj as ProblemDetails;

    // At least one of the core ProblemDetails properties must be present
    // to consider this a valid ProblemDetails object
    const hasCoreProperty =
        'type' in problem ||
        'title' in problem ||
        'status' in problem ||
        'detail' in problem ||
        'instance' in problem;

    if (!hasCoreProperty) {
        return false;
    }

    // Check required properties with allowed null/undefined values
    if (
        !(
            typeof problem.type === 'string' ||
            problem.type === null ||
            problem.type === undefined
        )
    ) {
        return false;
    }

    if (
        !(
            typeof problem.title === 'string' ||
            problem.title === null ||
            problem.title === undefined
        )
    ) {
        return false;
    }

    if (
        !(
            typeof problem.status === 'number' ||
            problem.status === null ||
            problem.status === undefined
        )
    ) {
        return false;
    }

    if (
        !(
            typeof problem.detail === 'string' ||
            problem.detail === null ||
            problem.detail === undefined
        )
    ) {
        return false;
    }

    if (
        !(
            typeof problem.instance === 'string' ||
            problem.instance === null ||
            problem.instance === undefined
        )
    ) {
        return false;
    }

    // Check optional properties
    if ('exception' in problem) {
        if (
            problem.exception !== null &&
            problem.exception !== undefined &&
            !isServiceErrorExceptionInfo(problem.exception)
        ) {
            return false;
        }
    }

    if ('errors' in problem) {
        if (problem.errors !== null && problem.errors !== undefined) {
            if (!Array.isArray(problem.errors)) {
                return false;
            }
            if (!problem.errors.every(isServiceError)) {
                return false;
            }
        }
    }

    return true;
}
